import {
  Box,
  Button,
  Flex,
  Table,
  Tbody,
  Td,
  Tooltip,
  Tr,
} from '@chakra-ui/react';
import {
  ColumnDef,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
  VisibilityState,
} from '@tanstack/react-table';
import React, { useEffect, useMemo, useState } from 'react';

import { date as commonDate } from '@finley-technologies/common';

import {
  BatchSettlementGQLType,
  BatchSettlementReportGQLType,
} from '../../../../../types/dashboard-core-gqlTypes.generated.d';
import { Icon } from 'packages/common-frontend/components/icons';
import { TanStackPaginationControl } from 'packages/common-frontend/components/TanStack';
import { Timestamp } from 'packages/common-frontend/components/Timestamp';
import { Typography } from 'packages/common-frontend/components/Typography';
import { useDownloadReportUrl } from 'packages/reporting/hooks/useDownloadReportUrl';
import { useGetReportS3DownloadUrlByReportingWorkleaseRecordIdQuery } from 'packages/reporting/requests/reporting.gql.generated';
import {
  TableBETA,
  TableHeaderBETA,
} from '~/packages/common-frontend/tables/TableBETA';

const SettlementReportDownloadButton = ({
  report,
}: {
  report: BatchSettlementReportGQLType;
}) => {
  const { getReportDownloadUrl } = useDownloadReportUrl(report);

  return (
    <Tooltip label="Download Settlement Report">
      <Button variant="v2-ghost" onClick={getReportDownloadUrl}>
        <Icon icon="arrow-down-to-bracket" />
      </Button>
    </Tooltip>
  );
};

const SettlementReportWorkleaseDownloadButton = ({
  workleaseId,
  refetchSettlementReports,
}: {
  workleaseId: string;
  refetchSettlementReports: () => void;
}) => {
  const [isPolling, setIsPolling] = useState(true);

  const {
    data: reportS3DownloadUrlByWorkleaseData,
    error: reportS3DownloadUrlByWorkleaseError,
  } = useGetReportS3DownloadUrlByReportingWorkleaseRecordIdQuery({
    pollInterval: isPolling ? 1000 : 0, // poll every second
    variables: {
      reportingWorkleaseRecordId: workleaseId,
      reportConfigType: 'batchSettlement',
    },
  });

  if (reportS3DownloadUrlByWorkleaseError) {
    throw reportS3DownloadUrlByWorkleaseError;
  }

  useEffect(() => {
    if (
      reportS3DownloadUrlByWorkleaseData
        ?.getReportS3DownloadUrlByReportingWorkleaseRecordId.workleaseStatus &&
      reportS3DownloadUrlByWorkleaseData
        ?.getReportS3DownloadUrlByReportingWorkleaseRecordId.workleaseStatus !==
        'initialized'
    ) {
      // once we know the worklease has been processed, refetch the settlement reports
      refetchSettlementReports();
      setIsPolling(false);
    }
  }, [reportS3DownloadUrlByWorkleaseData, refetchSettlementReports]);

  return (
    <Button
      variant="v2-ghost"
      // no on click for this button. purely placeholder
      isLoading={isPolling}
      isDisabled={
        (!isPolling && !reportS3DownloadUrlByWorkleaseData) ||
        reportS3DownloadUrlByWorkleaseData
          ?.getReportS3DownloadUrlByReportingWorkleaseRecordId
          .workleaseStatus === 'error'
      }
    >
      <Icon icon="arrow-down-to-bracket" />
    </Button>
  );
};

const getBaseColumnDefs = (
  refetchSettlementReports: () => void,
): ColumnDef<
  BatchSettlementGQLType & { workleaseId?: string | null },
  any
>[] => [
  {
    accessorKey: 'startDate',
    enableSorting: true,
    enableMultiSort: true,
    header: 'Start Date',
    cell: ({ getValue }) => commonDate.fullDateStrToHumanDateString(getValue()),
  },
  {
    accessorKey: 'endDate',
    enableSorting: true,
    enableMultiSort: true,
    header: 'End Date',
    cell: ({ getValue }) => commonDate.fullDateStrToHumanDateString(getValue()),
  },
  {
    accessorKey: 'capitalProviderName',
    header: 'Lender',
    enableSorting: true,
    enableMultiSort: true,
    cell: ({ getValue }) => getValue() ?? '-',
  },
  {
    accessorKey: 'loans',
    enableSorting: false,
    enableMultiSort: false,
    header: 'Loans',
    cell: function formatLoans({ getValue }) {
      const loans = getValue();
      if (!loans) {
        return '-';
      }

      const loanLabels: string[] = loans.map(
        (loan: { loanName: string; loanId: string; externalLoanId?: string }) =>
          loan.externalLoanId
            ? `${loan.loanName} (${loan.externalLoanId})`
            : loan.loanName,
      );

      const orderedLoanLabels = [...loanLabels].sort();

      return (
        <Flex direction="column" gap="0.5rem">
          {orderedLoanLabels.slice(0, 2).map((loanName: string) => {
            return (
              <Typography key={loanName} variant="base-muted">
                {loanName}
              </Typography>
            );
          })}

          {orderedLoanLabels.length > 2 && (
            <Tooltip
              sx={{
                placement: 'left',
                bg: 'transparent',
                boxShadow: 'none',
              }}
              label={
                <Box
                  sx={{
                    p: '1rem',
                    rounded: 'lg',
                    backgroundColor: 'aurora.500',
                  }}
                >
                  {orderedLoanLabels.slice(2).map((loanName: string) => (
                    <Flex key={loanName}>{loanName}</Flex>
                  ))}
                </Box>
              }
            >
              <Box
                borderBottom="1px dotted gray"
                width="fit-content"
                cursor="default"
              >
                Other Loans
              </Box>
            </Tooltip>
          )}
          {orderedLoanLabels.length === 0 && '-'}
        </Flex>
      );
    },
  },
  {
    accessorKey: 'created_at',
    enableSorting: true,
    enableMultiSort: true,
    header: 'Created At',
    cell: function formatCreatedAt({ getValue }) {
      const value = getValue();
      if (!value) return '-';
      return (
        <Timestamp
          timestamp={value}
          variant="datetime"
          TypographyProps={{ variant: 'base-muted' }}
        />
      );
    },
  },
  {
    accessorKey: 'report',
    enableSorting: false,
    enableMultiSort: false,
    header: 'Settlement Report',
    cell: function DownloadNoticeCell({ row, getValue }) {
      const report = getValue();

      if (!report) {
        const workleaseId: string | undefined = row.getValue('workleaseId');

        if (!workleaseId) {
          return (
            <Button variant="v2-ghost" isDisabled>
              <Icon icon="arrow-down-to-bracket" />
            </Button>
          );
        }

        return (
          <SettlementReportWorkleaseDownloadButton
            workleaseId={workleaseId}
            refetchSettlementReports={refetchSettlementReports}
          />
        );
      }

      return <SettlementReportDownloadButton report={report} />;
    },
  },
  {
    // column only used for internal state to fetch worklease status and report. never should be visible
    accessorKey: 'workleaseId',
    enableSorting: false,
    enableMultiSort: false,
    enableHiding: true,
  },
];

export const SettlementReportsTable = ({
  settlementReports,
  batchSettlementWorkleases,
  refetchSettlementReports,
}: {
  settlementReports: BatchSettlementGQLType[];
  batchSettlementWorkleases: {
    batchSettlementId: string;
    workleaseId?: string | null;
  }[];
  refetchSettlementReports: () => void;
}) => {
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'created_at', desc: true },
  ]);
  const [columnVisibility] = useState<VisibilityState>({
    workleaseId: false, // column only used for internal state. never should be visible
  });

  const expandedSettlementReportsData = useMemo(() => {
    return settlementReports.map((settlementReport) => {
      const batchSettlementWorklease = batchSettlementWorkleases.find(
        (worklease) => worklease.batchSettlementId === settlementReport.id,
      );

      return {
        ...settlementReport,
        workleaseId: batchSettlementWorklease?.workleaseId,
      };
    });
  }, [settlementReports, batchSettlementWorkleases]);

  const baseColumnDefs = useMemo(
    () => getBaseColumnDefs(refetchSettlementReports),
    [refetchSettlementReports],
  );

  const table = useReactTable({
    data: expandedSettlementReportsData,
    columns: baseColumnDefs,
    getCoreRowModel: getCoreRowModel(),
    enableSorting: true,
    enableMultiSort: true,
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    state: {
      sorting,
      columnVisibility,
    },
    initialState: {
      pagination: { pageIndex: 0, pageSize: 10 },
    },
  });

  if (!settlementReports.length) {
    return (
      <Table borderWidth="1px">
        <TableHeaderBETA table={table} />
        <Tbody>
          <Tr>
            <Td pl="1rem" colSpan={baseColumnDefs.length}>
              No settlement reports found.
            </Td>
          </Tr>
        </Tbody>
      </Table>
    );
  }

  return (
    <Flex direction="column" gap="0.5rem">
      <TableBETA table={table} />
      {settlementReports.length > 10 && (
        <TanStackPaginationControl table={table} />
      )}
    </Flex>
  );
};
